import {observer} from 'mobx-react-lite';
import React, {useState} from 'react';
import {EMenu, ETooltip, SubMenuActionsType} from 'webapp-lib/Components';
import {classy} from 'webapp-lib/utils';

import {ChevronSmallDown} from '_/assets/images';
import {PossibleFeatureFlagsType} from '_/hooks/useFeatureFlagCheck';
import {GlobalPermissionsType} from '_/utilities/types';

import {
  IconWrapper,
  SubNavButton,
  SubNavLink,
} from './SubNavigationBarItem.styles';

export enum Side {
  Left,
  Right,
}

export type SubNavItemProps<T extends object> = {
  active: boolean;
  item: SubNavItem<T>;
  onClick: (arg0: SubNavItem<T>) => void;
};

export type SubNavItem<T extends object = object> = T & {
  id: string;
  text: string;
  side?: Side;
  Icon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  requiredFeatureFlag?: PossibleFeatureFlagsType;
  requiredPermission?: GlobalPermissionsType;
  subItems?: (SubNavItem<T> & {selected?: boolean})[];
};

const USubNavigationBarItem = <T extends object>({
  onClick,
  item,
  active,
}: SubNavItemProps<T>): React.ReactElement => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  if (item.subItems && item.subItems.length > 0) {
    const subActions = item.subItems.map(
      (subItem): SubMenuActionsType => ({
        ...subItem,
        callback: () => onClick(subItem),
      })
    );

    const openMenu = (event: React.UIEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setAnchorEl(event.currentTarget);
    };

    const handleKeyEvent = (event: React.KeyboardEvent<HTMLElement>) => {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      openMenu(event);
    };

    return (
      <>
        <SubNavButton
          className={`SubNavButton ${classy({active})}`}
          data-testid={`SubNavButton-${item.id}`}
          onClick={openMenu}
          onKeyDown={handleKeyEvent}
        >
          {item.text}
          <IconWrapper>
            <ChevronSmallDown />
          </IconWrapper>
        </SubNavButton>
        <EMenu
          anchorEl={anchorEl}
          anchorOrigin={{
            horizontal: 'left',
            vertical: 'bottom',
          }}
          setAnchorEl={setAnchorEl}
          subActions={subActions}
          transformOrigin={{
            horizontal: 'left',
            vertical: 'top',
          }}
        />
      </>
    );
  }

  const handleClick = () => {
    onClick(item);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    handleClick();
  };

  return (
    <SubNavLink
      className={`SubNavLink ${classy({active})}`}
      data-testid={`SubNavLink-${item.id}`}
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      {item.Icon ? (
        <ETooltip title={item.text}>
          <item.Icon />
        </ETooltip>
      ) : (
        item.text
      )}
    </SubNavLink>
  );
};

export const SubNavigationBarItem = observer(USubNavigationBarItem);
